import { useEffect, useMemo, useState } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { directoryApi, vehiclesApi, usersApi, type CorporateAccount, type DriverDirectoryEntry, type Vehicle } from "@/lib/api";
import { CompanyCombobox } from "@/components/parcelio/company-combobox";
import { CommsPanel, type CommsMode } from "@/components/parcelio/comms-panel";
import {
  Plus,
  Search,
  Star,
  MapPin,
  Phone,
  MessageSquare,
  Video,
  Pencil,
  Mail,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";

type DriverStatus = DriverDirectoryEntry["status"];

// dp.status is a presence indicator (online / on_delivery / break / offline),
// not an account status — there's no separate account-status field in the
// merged directory response.
const presenceTone: Record<string, string> = {
  online: "bg-success/10 text-success border-success/20",
  on_delivery: "bg-info/10 text-info border-info/20",
  break: "bg-warning/15 text-warning-foreground border-warning/30",
  offline: "bg-muted text-muted-foreground border-border",
};

const presenceDot: Record<string, string> = {
  online: "bg-success",
  on_delivery: "bg-info",
  break: "bg-warning",
  offline: "bg-muted-foreground/50",
};

const presenceLabel: Record<string, string> = {
  online: "Available",
  on_delivery: "On delivery",
  break: "On break",
  offline: "Offline",
};

function generatePassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%^&*";
  const bytes = new Uint32Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join("");
}

function initialsOf(name: string) {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function DriverUsersPage() {
  const token = useAuth((s) => s.session?.accessToken);
  const [items, setItems] = useState<DriverDirectoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | DriverStatus>("all");
  const [comms, setComms] = useState<{ driver: DriverDirectoryEntry; mode: CommsMode } | null>(null);
  const [editing, setEditing] = useState<DriverDirectoryEntry | null>(null);
  const [creating, setCreating] = useState(false);
  const [corporates, setCorporates] = useState<CorporateAccount[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    let cancelled = false;
    directoryApi
      .drivers(token)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => toast.error("Failed to load drivers"))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    directoryApi.corporates(token).then((data) => {
      if (!cancelled) setCorporates(data);
    }).catch(() => {});
    vehiclesApi.list(token).then((data) => {
      if (!cancelled) setVehicles(data);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);

  const filtered = useMemo(
    () =>
      items.filter((u) => {
        if (status !== "all" && u.status !== status) return false;
        if (!q) return true;
        const s = q.toLowerCase();
        return (
          (u.email?.toLowerCase().includes(s) ?? false) ||
          u.name.toLowerCase().includes(s) ||
          (u.vehicle_plate?.toLowerCase().includes(s) ?? false)
        );
      }),
    [items, q, status],
  );

  const saveEdit = async (patch: { full_name: string; email: string; phone: string; corporate_account_id: string; vehicle_id: string; password: string }) => {
    if (!editing) return;
    if (patch.password && patch.password.length < 12) {
      toast.error("Password must be at least 12 characters");
      return;
    }
    try {
      const updated = await directoryApi.updateDriver(editing.id, {
        full_name: patch.full_name || undefined,
        email: patch.email || undefined,
        phone: patch.phone || undefined,
        corporate_account_id: patch.corporate_account_id || undefined,
        vehicle_id: patch.vehicle_id || undefined,
        password: patch.password || undefined,
      }, token);
      setItems((cur) => cur.map((u) => (u.id === editing.id ? updated : u)));
      setVehicles((cur) => cur.map((v) => {
        if (v.id === patch.vehicle_id) return { ...v, current_driver_id: editing.id };
        if (v.current_driver_id === editing.id && v.id !== patch.vehicle_id) return { ...v, current_driver_id: null };
        return v;
      }));
      toast.success("Driver updated");
      setEditing(null);
    } catch {
      toast.error("Could not update driver");
    }
  };

  const createDriver = async (form: { name: string; email: string; phone: string; corporate_account_id: string; password: string }) => {
    if (!form.name.trim() || (!form.email.trim() && !form.phone.trim())) {
      toast.error("Name and email or phone required");
      return;
    }
    if (!form.password.trim()) {
      toast.error("Generate a password first");
      return;
    }
    try {
      const created = await directoryApi.createDriver({
        full_name: form.name,
        email: form.email || undefined,
        phone: form.phone || undefined,
        corporate_account_id: form.corporate_account_id || undefined,
        password: form.password,
      }, token);
      setItems((cur) => [created, ...cur]);
      toast.success("Driver added");
      setCreating(false);
    } catch {
      toast.error("Could not add driver");
    }
  };

  const openComms = (driver: DriverDirectoryEntry | undefined, mode: CommsMode) => {
    if (!driver) return;
    setComms({ driver, mode });
  };

  const counts = {
    online: items.filter((u) => u.status === "online").length,
    on_delivery: items.filter((u) => u.status === "on_delivery").length,
    offline: items.filter((u) => u.status === "offline").length,
    total: items.length,
  };

  return (
    <>
      <Topbar
        title="Drivers"
        subtitle={`${counts.total} accounts · ${counts.online + counts.on_delivery} active now`}
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> Invite driver
          </Button>
        }
      />
      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { key: "total", label: "Total", value: counts.total },
            { key: "online", label: "Available", value: counts.online },
            { key: "on_delivery", label: "On delivery", value: counts.on_delivery },
            { key: "offline", label: "Offline", value: counts.offline },
          ].map((c) => (
            <div key={c.key} className="surface-card p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</div>
              <div className="mt-1 text-2xl font-bold">{c.value}</div>
            </div>
          ))}
        </div>

        <div className="surface-card">
          <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
            <div className="relative max-w-sm flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search driver, plate or email…"
                className="pl-9"
              />
            </div>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="online">Available</SelectItem>
                <SelectItem value="on_delivery">On delivery</SelectItem>
                <SelectItem value="break">On break</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <LoadingBlock label="Loading drivers…" />
          ) : (
            <div className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.map((u) => (
                <article key={u.id} className="rounded-xl border p-4 transition hover:shadow-md">
                  <div className="flex items-start gap-3">
                    <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold">
                      {initialsOf(u.name) || "??"}
                      <span
                        className={cn(
                          "absolute -right-0.5 -bottom-0.5 h-3.5 w-3.5 rounded-full ring-2 ring-surface",
                          presenceDot[u.status] ?? "bg-muted-foreground/50",
                        )}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="truncate font-semibold">{u.name}</h3>
                        <span
                          className={cn(
                            "shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
                            presenceTone[u.status] ?? "bg-muted text-muted-foreground border-border",
                          )}
                        >
                          {presenceLabel[u.status] ?? u.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Star className="h-3 w-3 fill-warning text-warning" /> {u.rating}
                        {u.vehicle_plate ? (
                          <span className="ml-1 rounded bg-muted px-1 py-px font-mono text-[10px]">
                            {u.vehicle_plate}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" /> {u.last_location ?? "Unknown"}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">{u.email ?? "—"}</div>
                    </div>
                  </div>

                  <dl className="mt-4 grid grid-cols-2 gap-2 rounded-lg bg-muted/40 p-3 text-center">
                    <div>
                      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Active parcels</dt>
                      <dd className="mt-0.5 font-medium">{u.active_parcels}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Completed today</dt>
                      <dd className="mt-0.5 font-medium">{u.completed_today}</dd>
                    </div>
                  </dl>

                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openComms(u, "voice")}
                      aria-label="Voice call"
                    >
                      <Phone className="mr-1 h-3.5 w-3.5" /> Call
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openComms(u, "chat")}
                      aria-label="Chat"
                    >
                      <MessageSquare className="mr-1 h-3.5 w-3.5" /> Chat
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => openComms(u, "video")}
                      aria-label="Video call"
                    >
                      <Video className="mr-1 h-3.5 w-3.5" /> Video
                    </Button>
                  </div>

                  <div className="mt-2 flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditing(u)}>
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                    </Button>
                    {u.email && !u.activated ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={async () => {
                          try {
                            await usersApi.resendActivation(u.id, token);
                            toast.success("Activation email resent");
                          } catch {
                            toast.error("Could not resend activation email");
                          }
                        }}
                      >
                        <Mail className="mr-1 h-3.5 w-3.5" /> Resend
                      </Button>
                    ) : null}
                  </div>
                </article>
              ))}
              {filtered.length === 0 ? (
                <div className="col-span-full p-8 text-center text-sm text-muted-foreground">
                  No drivers match your search.
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <CommsPanel
        open={!!comms}
        onOpenChange={(v) => !v && setComms(null)}
        peer={
          comms
            ? {
                name: comms.driver.name,
                initials: initialsOf(comms.driver.name),
                role: comms.driver.vehicle_plate ? `Driver · ${comms.driver.vehicle_plate}` : "Driver",
                meta: comms.driver.last_location ?? undefined,
              }
            : { name: "", initials: "" }
        }
        initialMode={comms?.mode ?? "chat"}
      />

      <EditDialog
        entry={editing}
        onClose={() => setEditing(null)}
        onSave={saveEdit}
        corporates={corporates}
        vehicles={vehicles}
      />

      <CreateDriverDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSave={createDriver}
        corporates={corporates}
      />
    </>
  );
}

function CreateDriverDialog({
  open,
  onClose,
  onSave,
  corporates,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (form: { name: string; email: string; phone: string; corporate_account_id: string; password: string }) => void;
  corporates: CorporateAccount[];
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [corporateAccountId, setCorporateAccountId] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (open) {
      setName("");
      setEmail("");
      setPhone("");
      setCorporateAccountId("");
      setPassword("");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite driver</DialogTitle>
          <DialogDescription>Creates a driver account, optionally attached to a corporate fleet.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="new-driver-name">Name</Label>
            <Input id="new-driver-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="new-driver-email">Login email</Label>
            <Input id="new-driver-email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="new-driver-phone">Phone</Label>
            <Input id="new-driver-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label>Password</Label>
            <div className="flex gap-2">
              <Input
                id="new-driver-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Generate a password"
              />
              <Button type="button" variant="outline" onClick={() => setPassword(generatePassword())}>
                Generate
              </Button>
            </div>
            {password ? (
              <p className="mt-1 text-[11px] text-muted-foreground">Share this with the driver — it won't be shown again.</p>
            ) : null}
          </div>
          <div className="grid gap-1.5">
            <Label>Corporate fleet (optional)</Label>
            <CompanyCombobox
              value={corporateAccountId}
              onChange={setCorporateAccountId}
              companies={corporates}
              placeholder="Platform driver (no corporate)"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave({ name, email, phone, corporate_account_id: corporateAccountId, password })}>Send invite</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  entry,
  onClose,
  onSave,
  corporates,
  vehicles,
}: {
  entry: DriverDirectoryEntry | null;
  onClose: () => void;
  onSave: (patch: { full_name: string; email: string; phone: string; corporate_account_id: string; vehicle_id: string; password: string }) => void;
  corporates: CorporateAccount[];
  vehicles: Vehicle[];
}) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [corporateAccountId, setCorporateAccountId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (entry) {
      setFullName(entry.name ?? "");
      setEmail(entry.email ?? "");
      setPhone(entry.phone ?? "");
      setCorporateAccountId(entry.corporate_account_id ?? "");
      setVehicleId(vehicles.find((v) => v.current_driver_id === entry.id)?.id ?? "");
      setPassword("");
    }
  }, [entry, vehicles]);

  const availableVehicles = vehicles.filter((v) => !v.current_driver_id || v.current_driver_id === entry?.id);

  return (
    <Dialog open={!!entry} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit driver account</DialogTitle>
          <DialogDescription>
            {entry?.name} · {entry?.id}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="edit-name">Name</Label>
            <Input id="edit-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="edit-email">Login email</Label>
            <Input id="edit-email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="edit-phone">Phone</Label>
            <Input id="edit-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="edit-password">New password (optional)</Label>
            <Input
              id="edit-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Leave blank to keep current password"
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Company</Label>
            <CompanyCombobox
              value={corporateAccountId}
              onChange={setCorporateAccountId}
              companies={corporates}
              placeholder="Platform driver (no corporate)"
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Vehicle</Label>
            <Select value={vehicleId || "none"} onValueChange={(v) => setVehicleId(v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {availableVehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.plate}{v.make ? ` · ${v.make} ${v.model ?? ""}`.trim() : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave({ full_name: fullName, email, phone, corporate_account_id: corporateAccountId, vehicle_id: vehicleId, password })}>Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
